import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Phone, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@quebon/ui/components/select'
import { Separator } from '@quebon/ui/components/separator'
import { Spinner } from '@quebon/ui/components/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'
import { Textarea } from '@quebon/ui/components/textarea'
import { ToggleGroup, ToggleGroupItem } from '@quebon/ui/components/toggle-group'
import { cn } from '@quebon/ui/lib/utils'

import {
  briefingSessionApi,
  type BriefingSessionAdmin,
  type BriefingSessionContact,
  type BriefingSessionInput,
  type SessionMode,
} from '@/api/lcMarketing'
import { BriefingAttendanceDialog } from './BriefingAttendanceDialog'
import {
  DEFAULT_SESSION_END_TIME,
  DEFAULT_SESSION_START_TIME,
  deriveSessionSchedule,
  hasValidSessionTimeRange,
  toKstDateTimeParts,
} from './briefingSessionSchedule'

type SessionDraft = {
  date: string
  startsAt: string
  endsAt: string
  applicationClosesAt: string
  mode: SessionMode
  capacity: string
}

type GeneratorDraft = {
  from: string
  to: string
  weekdays: number[]
}

type SessionCommand = 'close' | 'reopen' | 'cancel'

const EMPTY_DRAFT: SessionDraft = {
  date: '',
  startsAt: DEFAULT_SESSION_START_TIME,
  endsAt: DEFAULT_SESSION_END_TIME,
  applicationClosesAt: '00:00',
  mode: 'offline',
  capacity: '30',
}

const STATUS_LABEL: Record<string, string> = {
  published: '예정',
  completed: '완료',
  cancelled: '취소',
}

const AVAILABILITY_LABEL: Record<string, string> = {
  open: '접수 중',
  full: '정원 마감',
  closed: '접수 종료',
}

const API_ERROR_LABEL: Record<string, string> = {
  BRIEFING_SESSION_NOT_FOUND: '회차를 찾을 수 없습니다.',
  BRIEFING_SESSION_CLOSED: '이미 접수가 종료된 회차입니다.',
  BRIEFING_SESSION_FULL: '정원이 마감된 회차입니다.',
  BRIEFING_DUPLICATE_SESSION: '같은 전화번호로 이미 신청된 회차입니다.',
  BRIEFING_SESSION_VERSION_REQUIRED: '최신 회차 버전을 다시 불러와주세요.',
  BRIEFING_SESSION_STALE: '다른 운영자가 먼저 변경했습니다. 최신 값을 다시 불러왔습니다.',
  BRIEFING_SESSION_DATE_CONFLICT: '같은 날짜에 이미 게시된 회차가 있습니다.',
  BRIEFING_SESSION_CAPACITY_BELOW_ACTIVE: '현재 신청자 수보다 정원을 낮출 수 없습니다.',
  BRIEFING_SESSION_TIME_INVALID: '개최 시작·종료 시간을 확인해주세요.',
  BRIEFING_SESSION_REOPEN_NOT_ALLOWED: '마감·정원·상태 조건 때문에 접수를 재개할 수 없습니다.',
  BRIEFING_CONTACT_STALE: '다른 운영자가 전화 결과를 먼저 변경했습니다.',
}

const PUBLIC_WINDOW_DESCRIPTION =
  'LC 홈페이지에는 온라인·오프라인별 가까운 회차 2개씩 공개됩니다. 정원 마감 회차도 공개 순번에 포함됩니다.'

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

function apiErrorMessage(error: unknown, fallback: string): string {
  const code = (error as { response?: { data?: { error?: { code?: string } } } })
    .response?.data?.error?.code
  return code ? API_ERROR_LABEL[code] ?? fallback : fallback
}

export default function BriefingSessionsTab() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState('')
  const [mode, setMode] = useState<SessionMode | ''>('')
  const [availability, setAvailability] = useState<'open' | 'full' | 'closed' | ''>('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [drafts, setDrafts] = useState<SessionDraft[]>([])
  const [draft, setDraft] = useState(EMPTY_DRAFT)
  const [generator, setGenerator] = useState<GeneratorDraft>({ from: '', to: '', weekdays: [] })
  const [editing, setEditing] = useState<BriefingSessionAdmin | null>(null)
  const [contactChangeId, setContactChangeId] = useState<number | null>(null)
  const [attendanceSession, setAttendanceSession] = useState<BriefingSessionAdmin | null>(null)

  const sessionsQuery = useQuery({
    queryKey: ['briefing-sessions-admin', { status, mode, availability, from, to }],
    queryFn: () => briefingSessionApi.list({
      status: status || undefined,
      mode: mode || undefined,
      availability: availability || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
  })

  const previewQuery = useQuery({
    queryKey: ['briefing-session-create-preview', drafts],
    queryFn: () => briefingSessionApi.preview(drafts.map(toInput)),
    enabled: drafts.length > 0,
  })

  const createMutation = useMutation({
    mutationFn: async () => {
      const preview = previewQuery.data
      if (!preview || preview.sessions.some((item) => item.dateConflict)) {
        throw new Error('DATE_CONFLICT')
      }
      return briefingSessionApi.create(drafts.map(toInput), preview.checksum)
    },
    onSuccess: () => {
      setDrafts([])
      queryClient.invalidateQueries({ queryKey: ['briefing-sessions-admin'] })
      toast.success('회차를 게시했습니다.')
    },
    onError: (error) => toast.error(apiErrorMessage(error, '충돌을 확인한 뒤 다시 시도해주세요.')),
  })

  const commandMutation = useMutation({
    mutationFn: async ({ session, command }: { session: BriefingSessionAdmin; command: SessionCommand }) => {
      if (command === 'cancel') {
        await briefingSessionApi.cancel(session.id, session.version)
        return
      }
      await briefingSessionApi.setClosed(session.id, session.version, command === 'close')
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['briefing-sessions-admin'] }),
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['briefing-sessions-admin'] })
      toast.error(apiErrorMessage(error, '다른 운영자의 변경 여부를 확인하고 다시 시도해주세요.'))
    },
  })

  const addDraft = () => {
    if (!draft.date) return
    setDrafts((current) => [...current, draft])
    setDraft((current) => ({
      ...EMPTY_DRAFT,
      mode: current.mode,
      applicationClosesAt: current.mode === 'offline' ? '00:00' : '10:00',
      capacity: current.mode === 'offline' ? '30' : '',
    }))
  }

  const generateDrafts = () => {
    if (!generator.from || !generator.to || generator.from > generator.to || generator.weekdays.length === 0) return
    const [startYear, startMonth, startDay] = generator.from.split('-').map(Number)
    const [endYear, endMonth, endDay] = generator.to.split('-').map(Number)
    const cursor = new Date(Date.UTC(startYear, startMonth - 1, startDay))
    const end = new Date(Date.UTC(endYear, endMonth - 1, endDay))
    const generated: SessionDraft[] = []

    while (cursor <= end) {
      if (generator.weekdays.includes(cursor.getUTCDay())) {
        generated.push({ ...draft, date: cursor.toISOString().slice(0, 10) })
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1)
    }
    setDrafts((current) => [...current, ...generated])
  }

  const sessions = [...(sessionsQuery.data ?? [])].sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt))
  const upcoming = sessions.filter((session) => session.effectiveStatus === 'published')
  const history = sessions.filter((session) => session.effectiveStatus !== 'published')

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="border-b">
          <CardTitle>사업설명회 회차 게시</CardTitle>
          <CardDescription>개별 회차를 후보에 추가하거나 기간과 요일로 여러 회차를 생성합니다.</CardDescription>
          <CardAction>
            <Badge variant="outline">확인 즉시 게시</Badge>
          </CardAction>
        </CardHeader>

        <CardContent>
          <FieldGroup className="grid gap-4 lg:grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr_0.8fr_auto]">
            <Field>
              <FieldLabel htmlFor="briefing-date">날짜</FieldLabel>
              <Input id="briefing-date" type="date" value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} />
            </Field>
            <ModeField
              id="briefing-mode"
              value={draft.mode}
              onChange={(next) => setDraft({
                ...draft,
                mode: next,
                applicationClosesAt: next === 'offline' ? '00:00' : '10:00',
                capacity: next === 'offline' ? '30' : '',
              })}
            />
            <TimeField id="briefing-start-time" label="시작" value={draft.startsAt} onChange={(startsAt) => setDraft({ ...draft, startsAt })} />
            <TimeField id="briefing-end-time" label="종료" value={draft.endsAt} onChange={(endsAt) => setDraft({ ...draft, endsAt })} />
            <TimeField id="briefing-close-time" label="접수 마감" value={draft.applicationClosesAt} onChange={(applicationClosesAt) => setDraft({ ...draft, applicationClosesAt })} />
            <Field>
              <FieldLabel htmlFor="briefing-capacity">정원</FieldLabel>
              <Input id="briefing-capacity" type="number" min="1" value={draft.capacity} onChange={(event) => setDraft({ ...draft, capacity: event.target.value })} placeholder="제한 없음" />
            </Field>
            <Field className="justify-end">
              <FieldLabel className="sr-only">후보 추가</FieldLabel>
              <Button type="button" onClick={addDraft} disabled={!draft.date}>
                <Plus data-icon="inline-start" />
                후보 추가
              </Button>
            </Field>
          </FieldGroup>
        </CardContent>

        <Separator />

        <CardContent>
          <FieldGroup className="grid items-end gap-4 lg:grid-cols-[1fr_1fr_auto_auto]">
            <Field>
              <FieldLabel htmlFor="briefing-bulk-from">일괄 시작일</FieldLabel>
              <Input id="briefing-bulk-from" type="date" value={generator.from} onChange={(event) => setGenerator({ ...generator, from: event.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="briefing-bulk-to">일괄 종료일</FieldLabel>
              <Input id="briefing-bulk-to" type="date" value={generator.to} onChange={(event) => setGenerator({ ...generator, to: event.target.value })} />
            </Field>
            <FieldSet className="gap-2">
              <FieldLegend variant="label">요일</FieldLegend>
              <ToggleGroup
                aria-label="회차 생성 요일"
                variant="outline"
                value={generator.weekdays.map(String)}
                onValueChange={(values) => setGenerator({ ...generator, weekdays: values.map(Number) })}
              >
                {WEEKDAYS.map((label, weekday) => (
                  <ToggleGroupItem key={label} value={String(weekday)} aria-label={`${label}요일`}>
                    {label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </FieldSet>
            <Button type="button" variant="outline" onClick={generateDrafts} disabled={!generator.from || !generator.to || generator.from > generator.to || generator.weekdays.length === 0}>
              현재 방식·시간으로 후보 생성
            </Button>
          </FieldGroup>
        </CardContent>

        {drafts.length > 0 ? (
          <>
            <Separator />
            <CardContent className="flex flex-col gap-4">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {drafts.map((item, index) => {
                  const preview = previewQuery.data?.sessions[index]
                  const previewLabel = preview?.dateConflict
                    ? '같은 날짜의 게시 회차와 충돌'
                    : preview?.inPublicWindowAfterCreate
                      ? '게시 즉시 홈페이지 공개'
                      : '홈페이지 공개 범위 밖'

                  return (
                    <Card key={`${item.date}-${item.mode}-${index}`} size="sm">
                      <CardHeader>
                        <CardTitle>{item.date}</CardTitle>
                        <CardDescription>{item.startsAt}–{item.endsAt} · {item.mode === 'offline' ? '오프라인' : '온라인'}</CardDescription>
                        <CardAction>
                          <Button type="button" variant="ghost" size="xs" onClick={() => setDrafts((current) => current.filter((_, draftIndex) => draftIndex !== index))}>
                            제외
                          </Button>
                        </CardAction>
                      </CardHeader>
                      <CardContent>
                        <Badge variant={preview?.dateConflict ? 'destructive' : 'secondary'}>{previewLabel}</Badge>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="button" onClick={() => createMutation.mutate()} disabled={!previewQuery.data || previewQuery.data.sessions.some((item) => item.dateConflict) || createMutation.isPending}>
                {createMutation.isPending ? <Spinner data-icon="inline-start" /> : <Check data-icon="inline-start" />}
                {drafts.length}개 회차 게시
              </Button>
            </CardFooter>
          </>
        ) : null}
      </Card>

      <SessionFilters
        status={status}
        mode={mode}
        availability={availability}
        from={from}
        to={to}
        count={sessions.length}
        onStatusChange={setStatus}
        onModeChange={setMode}
        onAvailabilityChange={setAvailability}
        onFromChange={setFrom}
        onToChange={setTo}
      />

      {sessionsQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Spinner />
          회차를 불러오는 중입니다.
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <SessionGroup title="예정 회차" description={PUBLIC_WINDOW_DESCRIPTION} sessions={upcoming} onEdit={setEditing} onCommand={(session, command) => commandMutation.mutate({ session, command })} onContacts={setContactChangeId} onAttendance={setAttendanceSession} />
          {history.length > 0 ? (
            <SessionGroup title="완료·취소 이력" sessions={history} onEdit={setEditing} onCommand={(session, command) => commandMutation.mutate({ session, command })} onContacts={setContactChangeId} onAttendance={setAttendanceSession} muted />
          ) : null}
        </div>
      )}

      {editing ? (
        <EditSessionDialog
          session={editing}
          onClose={() => setEditing(null)}
          onSaved={(result) => {
            setEditing(null)
            queryClient.invalidateQueries({ queryKey: ['briefing-sessions-admin'] })
            if (result.latestChangeId && result.activeApplicationCount > 0) setContactChangeId(result.latestChangeId)
          }}
        />
      ) : null}
      {attendanceSession && <BriefingAttendanceDialog session={attendanceSession} onClose={() => setAttendanceSession(null)} />}
      {contactChangeId ? <ContactDialog changeId={contactChangeId} sessions={sessions.filter((session) => session.applicationAvailability === 'open')} onClose={() => setContactChangeId(null)} /> : null}
    </div>
  )
}

function SessionFilters({
  status,
  mode,
  availability,
  from,
  to,
  count,
  onStatusChange,
  onModeChange,
  onAvailabilityChange,
  onFromChange,
  onToChange,
}: {
  status: string
  mode: SessionMode | ''
  availability: 'open' | 'full' | 'closed' | ''
  from: string
  to: string
  count: number
  onStatusChange: (value: string) => void
  onModeChange: (value: SessionMode | '') => void
  onAvailabilityChange: (value: 'open' | 'full' | 'closed' | '') => void
  onFromChange: (value: string) => void
  onToChange: (value: string) => void
}) {
  return (
    <Card size="sm">
      <CardContent>
        <FieldGroup className="grid items-end gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_auto_1fr_auto]">
          <CompactSelect
            label="상태"
            value={status || 'all'}
            items={[{ value: 'all', label: '전체 상태' }, { value: 'published', label: '예정' }, { value: 'completed', label: '완료' }, { value: 'cancelled', label: '취소' }]}
            onChange={(value) => onStatusChange(value === 'all' ? '' : value)}
          />
          <CompactSelect
            label="방식"
            value={mode || 'all'}
            items={[{ value: 'all', label: '전체 방식' }, { value: 'offline', label: '오프라인' }, { value: 'online', label: '온라인' }]}
            onChange={(value) => onModeChange(value === 'all' ? '' : value as SessionMode)}
          />
          <CompactSelect
            label="접수 상태"
            value={availability || 'all'}
            items={[{ value: 'all', label: '전체 접수 상태' }, { value: 'open', label: '접수 중' }, { value: 'full', label: '정원 마감' }, { value: 'closed', label: '접수 종료' }]}
            onChange={(value) => onAvailabilityChange(value === 'all' ? '' : value as typeof availability)}
          />
          <Field>
            <FieldLabel htmlFor="briefing-filter-from">시작 날짜</FieldLabel>
            <Input id="briefing-filter-from" type="date" value={from} onChange={(event) => onFromChange(event.target.value)} />
          </Field>
          <span className="hidden pb-2 text-center text-muted-foreground xl:block">–</span>
          <Field>
            <FieldLabel htmlFor="briefing-filter-to">종료 날짜</FieldLabel>
            <Input id="briefing-filter-to" type="date" value={to} onChange={(event) => onToChange(event.target.value)} />
          </Field>
          <Badge variant="outline" className="mb-1 justify-self-start tabular-nums xl:justify-self-end">{count}개 회차</Badge>
        </FieldGroup>
      </CardContent>
    </Card>
  )
}

function SessionGroup({
  title,
  description,
  sessions,
  onEdit,
  onCommand,
  onContacts,
  onAttendance,
  muted = false,
}: {
  title: string
  description?: string
  sessions: BriefingSessionAdmin[]
  onEdit: (session: BriefingSessionAdmin) => void
  onCommand: (session: BriefingSessionAdmin, command: SessionCommand) => void
  onContacts: (changeId: number) => void
  onAttendance: (session: BriefingSessionAdmin) => void
  muted?: boolean
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      <Card className="py-0">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead className="px-4">개최</TableHead>
              <TableHead>방식</TableHead>
              <TableHead>접수 마감</TableHead>
              <TableHead>신청/정원</TableHead>
              <TableHead>참석 · 미기록</TableHead>
              <TableHead>LC 홈페이지</TableHead>
              <TableHead>상태</TableHead>
              <TableHead className="px-4 text-right">운영</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">조건에 맞는 회차가 없습니다.</TableCell>
              </TableRow>
            ) : sessions.map((session) => (
              <TableRow key={session.id} className={cn(muted && 'text-muted-foreground')}>
                <TableCell className="px-4">
                  <Button type="button" variant="link" className="h-auto flex-col items-start" onClick={() => onAttendance(session)}>
                    <span>{formatDateTime(session.startsAt)}</span>
                    <span className="text-xs text-muted-foreground">~ {formatTime(session.endsAt)}</span>
                  </Button>
                </TableCell>
                <TableCell>{session.mode === 'offline' ? '오프라인' : '온라인'}</TableCell>
                <TableCell>{formatDateTime(session.applicationClosesAt)}</TableCell>
                <TableCell className="tabular-nums">{session.activeApplicationCount}/{session.capacity ?? '∞'}</TableCell>
                <TableCell className="tabular-nums">
                  참석 {session.attendedCount} / 신청 {session.activeApplicationCount}
                  <div className="text-xs text-muted-foreground">미기록 {session.unknownAttendanceCount}</div>
                </TableCell>
                <TableCell>{session.inPublicWindow ? <Badge variant="secondary">공개 중</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell>
                  <span className="font-medium">{STATUS_LABEL[session.effectiveStatus]}</span>
                  <div className="text-xs text-muted-foreground">{AVAILABILITY_LABEL[session.applicationAvailability]}</div>
                </TableCell>
                <TableCell className="px-4">
                  <div className="flex justify-end gap-1">
                    <Button type="button" variant="outline" size="xs" onClick={() => onAttendance(session)}>출석부</Button>
                    {session.latestChangeId ? (
                      <Button type="button" variant="outline" size="xs" onClick={() => onContacts(session.latestChangeId!)}>
                        <Phone data-icon="inline-start" />
                        연락
                      </Button>
                    ) : null}
                    {session.effectiveStatus === 'published' ? (
                      <>
                        <Button type="button" variant="outline" size="xs" onClick={() => onEdit(session)}>변경</Button>
                        <Button type="button" variant="outline" size="xs" onClick={() => onCommand(session, session.manuallyClosed ? 'reopen' : 'close')}>
                          {session.manuallyClosed ? '접수 재개' : '접수 종료'}
                        </Button>
                        <Button type="button" variant="destructive" size="xs" onClick={() => window.confirm('회차를 즉시 취소할까요?') && onCommand(session, 'cancel')}>
                          취소
                        </Button>
                      </>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </section>
  )
}

function EditSessionDialog({ session, onClose, onSaved }: { session: BriefingSessionAdmin; onClose: () => void; onSaved: (session: BriefingSessionAdmin) => void }) {
  const queryClient = useQueryClient()
  const initial = useMemo(() => {
    const start = toKstDateTimeParts(session.startsAt)
    const end = toKstDateTimeParts(session.endsAt)
    return {
      date: start.date,
      startTime: start.time || DEFAULT_SESSION_START_TIME,
      endTime: end.time || DEFAULT_SESSION_END_TIME,
      mode: session.mode,
      capacity: session.capacity?.toString() ?? '',
      reason: '',
    }
  }, [session])
  const [form, setForm] = useState(initial)
  const validTimeRange = hasValidSessionTimeRange(form.startTime, form.endTime)
  const schedule = form.date && validTimeRange ? deriveSessionSchedule(form.date, form.startTime, form.endTime, form.mode) : null

  const mutation = useMutation({
    mutationFn: async () => {
      if (!schedule) throw new Error('INVALID_TIME')
      const payload = {
        ...schedule,
        mode: form.mode,
        capacity: form.capacity ? Number(form.capacity) : null,
        expectedVersion: session.version,
        reason: form.reason || undefined,
      }
      const preview = await briefingSessionApi.changePreview(session.id, payload)
      if (preview.capacityConflict || preview.dateConflict) throw new Error('CONFLICT')
      const publicWindowMessage = preview.inPublicWindowAfterChange ? '변경 후 LC 홈페이지에 공개됩니다.' : '변경 후 LC 홈페이지 공개 범위 밖입니다.'
      const contactMessage = preview.contactTargets.length > 0
        ? `전화 확인 대상 ${preview.contactTargetCount}명\n${preview.contactTargets.map((target) => `${target.applicantName} ${target.phone}`).join('\n')}`
        : '전화 확인 대상이 없습니다.'
      if (!window.confirm(`${publicWindowMessage}\n\n${contactMessage}\n\n변경할까요?`)) throw new Error('CANCELLED')
      return briefingSessionApi.update(session.id, payload)
    },
    onSuccess: onSaved,
    onError: (error) => {
      if ((error as Error).message !== 'CANCELLED') {
        queryClient.invalidateQueries({ queryKey: ['briefing-sessions-admin'] })
        toast.error(apiErrorMessage(error, '변경 충돌 또는 최신 버전을 확인해주세요.'))
      }
    },
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>회차 변경 영향 확인</DialogTitle>
          <DialogDescription>개최 일자와 시간만 선택하면 접수 마감이 자동으로 계산됩니다.</DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="edit-briefing-date">개최 일자</FieldLabel>
            <Input id="edit-briefing-date" type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
          </Field>
          <FieldSet className="gap-2">
            <div className="flex items-center justify-between gap-3">
              <FieldLegend variant="label">개최 시간</FieldLegend>
              <Badge variant="secondary">기본 10:30–12:00</Badge>
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
              <TimeField id="edit-briefing-start-time" label="시작" value={form.startTime} onChange={(startTime) => setForm({ ...form, startTime })} />
              <span className="pb-2 text-muted-foreground">→</span>
              <TimeField id="edit-briefing-end-time" label="종료" value={form.endTime} onChange={(endTime) => setForm({ ...form, endTime })} />
            </div>
            {!validTimeRange ? <FieldError>종료 시간은 시작 시간보다 늦어야 합니다.</FieldError> : null}
          </FieldSet>

          <Alert>
            <AlertTitle>접수 마감 · 자동</AlertTitle>
            <AlertDescription>
              <strong className="font-semibold text-foreground">{schedule ? formatDateTime(schedule.applicationClosesAt) : '날짜와 시간을 확인해주세요'}</strong>
              <p>{form.mode === 'offline' ? '오프라인은 개최일 00:00에 마감됩니다.' : '온라인은 시작 30분 전에 마감됩니다.'}</p>
            </AlertDescription>
          </Alert>

          <div className="grid gap-4 sm:grid-cols-2">
            <ModeField id="edit-briefing-mode" value={form.mode} onChange={(mode) => setForm({ ...form, mode, capacity: mode === 'offline' ? form.capacity || '30' : '' })} />
            <Field>
              <FieldLabel htmlFor="edit-briefing-capacity">정원</FieldLabel>
              <Input id="edit-briefing-capacity" type="number" min="1" value={form.capacity} onChange={(event) => setForm({ ...form, capacity: event.target.value })} placeholder="제한 없음" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="edit-briefing-reason">변경 사유 (선택)</FieldLabel>
            <Textarea id="edit-briefing-reason" value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} />
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>닫기</Button>
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending || !schedule}>
            {mutation.isPending ? <Spinner data-icon="inline-start" /> : null}
            영향 확인 후 변경
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ContactDialog({ changeId, sessions, onClose }: { changeId: number; sessions: BriefingSessionAdmin[]; onClose: () => void }) {
  const queryClient = useQueryClient()
  const contacts = useQuery({ queryKey: ['briefing-session-contacts', changeId], queryFn: () => briefingSessionApi.contacts(changeId) })
  const mutation = useMutation({
    mutationFn: ({ contact, outcome, targetSessionId }: { contact: BriefingSessionContact; outcome: BriefingSessionContact['outcome']; targetSessionId?: string }) => briefingSessionApi.updateContact(contact.id, { expectedVersion: contact.version, outcome, targetSessionId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['briefing-session-contacts', changeId] }),
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['briefing-session-contacts', changeId] })
      toast.error(apiErrorMessage(error, '전화 처리 결과를 저장하지 못했습니다.'))
    },
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>회차 변경 전화 처리</DialogTitle>
          <DialogDescription>연락 미완료는 목록에 계속 남습니다.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {contacts.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground"><Spinner />연락 대상을 불러오는 중입니다.</div>
          ) : contacts.data?.length ? contacts.data.map((contact) => (
            <ContactRow key={contact.id} contact={contact} sessions={sessions} disabled={mutation.isPending} onSave={(outcome, targetSessionId) => mutation.mutate({ contact, outcome, targetSessionId })} />
          )) : (
            <p className="py-10 text-center text-sm text-muted-foreground">처리할 연락 대상이 없습니다.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ContactRow({ contact, sessions, disabled, onSave }: { contact: BriefingSessionContact; sessions: BriefingSessionAdmin[]; disabled: boolean; onSave: (outcome: BriefingSessionContact['outcome'], targetSessionId?: string) => void }) {
  const [outcome, setOutcome] = useState<BriefingSessionContact['outcome']>(contact.outcome === 'pending' ? 'attending' : contact.outcome)
  const [target, setTarget] = useState(contact.targetSessionId ?? '')
  const needsAttention = contact.outcome === 'pending' || contact.outcome === 'unreached'

  return (
    <Card size="sm" className={cn(needsAttention && 'bg-accent')}>
      <CardContent className="grid items-center gap-3 md:grid-cols-[1fr_1.4fr_auto]">
        <div>
          <strong>{contact.applicantName}</strong>
          <p className="text-sm text-muted-foreground">{contact.phone}</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Select value={outcome} onValueChange={(value) => { if (value) setOutcome(value as BriefingSessionContact['outcome']) }}>
            <SelectTrigger className="w-full" aria-label="전화 처리 결과"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="attending">변경 일정 참석</SelectItem>
                <SelectItem value="moved">다른 회차 이동</SelectItem>
                <SelectItem value="cancelled">접수 취소</SelectItem>
                <SelectItem value="unreached">연락 미완료</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          {outcome === 'moved' ? (
            <Select value={target || 'none'} onValueChange={(value) => setTarget(!value || value === 'none' ? '' : value)}>
              <SelectTrigger className="w-full" aria-label="이동 회차"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="none">이동 회차 선택</SelectItem>
                  {sessions.map((session) => <SelectItem key={session.sessionId} value={session.sessionId}>{formatDateTime(session.startsAt)}</SelectItem>)}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : null}
        </div>
        <Button type="button" size="sm" disabled={disabled || outcome === 'moved' && !target} onClick={() => onSave(outcome, target || undefined)}>결과 저장</Button>
      </CardContent>
    </Card>
  )
}

function ModeField({ id, value, onChange }: { id: string; value: SessionMode; onChange: (value: SessionMode) => void }) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>방식</FieldLabel>
      <Select value={value} onValueChange={(next) => { if (next) onChange(next as SessionMode) }}>
        <SelectTrigger id={id} className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="offline">오프라인</SelectItem>
            <SelectItem value="online">온라인</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  )
}

function CompactSelect({ label, value, items, onChange }: { label: string; value: string; items: Array<{ value: string; label: string }>; onChange: (value: string) => void }) {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Select value={value} onValueChange={(next) => { if (next) onChange(next) }}>
        <SelectTrigger className="w-full" aria-label={label}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectGroup>{items.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  )
}

function TimeField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (value: string) => void }) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} type="time" value={value} onChange={(event) => onChange(event.target.value)} />
    </Field>
  )
}

function toInput(draft: SessionDraft): BriefingSessionInput {
  const capacity = draft.capacity ? Number(draft.capacity) : null
  return {
    startsAt: fromKstInput(`${draft.date}T${draft.startsAt}`),
    endsAt: fromKstInput(`${draft.date}T${draft.endsAt}`),
    applicationClosesAt: fromKstInput(`${draft.date}T${draft.applicationClosesAt}`),
    mode: draft.mode,
    capacity,
  }
}

function fromKstInput(value: string) {
  return new Date(`${value}:00+09:00`).toISOString()
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}
